import {
  ButtonComponent,
  SelectComponent,
  SpinnerComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormErrorsType } from "./interfaces";
import {
  setEstimatedAnnualTurnoverValidation,
  setStaffSizeValidation,
} from "@zenith/features/Tiers/validations/BusinessInformationValidations";
import {
  OnboardingApiService,
  OnboardingBusinessInfoDTO,
} from "@zenith/services/user";
import { ACCOUNT_CREATED, SIGN_UP } from "@zenith/routes/paths";
import {
  useMobileVerificationContext,
  useNotificationContext,
  useSignUpContext,
} from "@zenith/context";
import { countryCodeToEnum } from "@zenith/utils";
import {
  CONNECTION_ERROR_MESSAGE,
  SUCCESS_API_CODE,
} from "@zenith/data/constants";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import useAppNavigation from "@zenith/hooks/appNavigation";
import { estimatedAnnualTurnoverItems, staffSizeItems } from "../utils";
import { useLocation } from "react-router-dom";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";

type FormField = "estimatedAnnualTurnover" | "staffStrength";

interface InitialFormData {
  estimatedAnnualTurnover: OnboardingBusinessInfoDTO.estimatedTurnover | "";
  staffStrength: OnboardingBusinessInfoDTO.staffStrength | "";
}

const SignUpBusinessInformationpage = () => {
  const {
    signUpContext: { userExternalId },
  } = useSignUpContext();
  const { navigateTo } = useAppNavigation();
  const location = useLocation();

  const accountCreationStartedRef = useRef<boolean>(false);
  const registrationSteps = location.state?.signupType;

  const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
  const [isAccountCreationFailed, setIsAccountCreationFailed] =
    useState<boolean>(false);

  const {
    mobileVerificationContext: { phoneNumber, countryCode },
    setFirstName,
  } = useMobileVerificationContext();

  const { setOpen } = useNotificationContext();
  const initialFormData: InitialFormData = {
    estimatedAnnualTurnover: "",
    staffStrength: "",
  };

  const [accountCreationError, setAccountCreationError] = useState<string>("");

  const [formData, setFormData] = useState<InitialFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<FormErrorsType>({
    estimatedAnnualTurnover: { message: "", hasError: false },
    staffStrength: { message: "", hasError: false },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formDataRef = useRef<InitialFormData>(initialFormData);

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({ title: "", message: "", status: "success" });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({ title, message, status });
    setOpen(true);
  }
  const [currentStep, setCurrentStep] = useState<string>();

  const validateField = (
    field: FormField,
    value: string
  ): { hasError: boolean; message: string } => {
    switch (field) {
      case "estimatedAnnualTurnover":
        return setEstimatedAnnualTurnoverValidation(value);
      case "staffStrength":
        return setStaffSizeValidation(value);

      default:
        console.warn("Field not found");
        return { hasError: false, message: "" };
    }
  };

  const handleFormErrors = (field: FormField, value: string) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [field]: validateField(field, value),
    }));
  };

  const onFieldBlur = (field: FormField) => {
    const formDataField = formDataRef.current[field];
    if (formDataField === undefined) return;
    handleFormErrors(field, formDataField);
  };

  const onFieldChange = (
    field: FormField,
    event?: React.ChangeEvent<HTMLInputElement> | string | null
  ) => {
    let updatedEventValue = "";

    if (
      event &&
      typeof event === "object" &&
      "target" in event &&
      event.target instanceof HTMLInputElement
    ) {
      updatedEventValue = event.target.value;
    } else if (typeof event === "string") {
      updatedEventValue = event;
    }

    switch (field) {
      case "estimatedAnnualTurnover":
      case "staffStrength":
        break;

      default:
        console.warn("Field not found");
    }

    formDataRef.current = {
      ...formDataRef.current,
      [field]: updatedEventValue,
    };

    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: updatedEventValue,
    }));

    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [field]: { message: "", hasError: false },
    }));
  };

  const isFormValid =
    !!formData.estimatedAnnualTurnover &&
    !!formData.staffStrength &&
    !formErrors.estimatedAnnualTurnover?.hasError &&
    !formErrors.staffStrength.hasError;

  const handleCreateAccount = useCallback(async () => {
    if (accountCreationStartedRef.current) return;
    accountCreationStartedRef.current = true;

    setIsCreatingAccount(true);
    setIsAccountCreationFailed(false);

    try {
      const accountResponse = await OnboardingApiService.createAccount({
        phoneNumber,
        countryCode: countryCodeToEnum(countryCode),
      });

      if (
        "data" in accountResponse &&
        accountResponse.data &&
        accountResponse.internalCode === SUCCESS_API_CODE
      ) {
        setFirstName(accountResponse.data.firstName as string);
        navigateTo(ACCOUNT_CREATED, {
          state: { accountInfo: accountResponse.data },
        });
      }
    } catch (error: any) {
      const apiErrorMessage = error?.body?.message || CONNECTION_ERROR_MESSAGE;
      setAccountCreationError(apiErrorMessage);
      setIsAccountCreationFailed(true);
    } finally {
      setIsCreatingAccount(false);
      accountCreationStartedRef.current = false;
    }
  }, [phoneNumber, countryCode, navigateTo, setFirstName]);

  const handleContinue = async () => {
    if (!formData.estimatedAnnualTurnover || !formData.staffStrength) {
      return;
    }

    const requestBody: OnboardingBusinessInfoDTO = {
      estimatedTurnover: formData.estimatedAnnualTurnover,
      staffStrength: formData.staffStrength,
      userExternalId: userExternalId,
    };

    setIsSubmitting(true);
    try {
      const response = await OnboardingApiService.setBusinessInformation(
        requestBody
      );
      if (response.success && response.internalCode === SUCCESS_API_CODE) {
        handleCreateAccount();
      }
    } catch (error: any) {
      console.error(error);
      notify(
        "Onboarding Error",
        error?.body?.message || CONNECTION_ERROR_MESSAGE,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscardAll = () => {
    accountCreationStartedRef.current = false;
    formDataRef.current = {
      estimatedAnnualTurnover: "",
      staffStrength: "",
    };
    setIsAccountCreationFailed(false);
    setAccountCreationError("");
    navigateTo(SIGN_UP);
  };

  useEffect(() => {
    const createAccountStep = registrationSteps?.find(
      (step: { stepName: string; status: string }) =>
        step.stepName === "PENDING_CREATE_ACCOUNT"
    );

    if (createAccountStep?.status === "IN_PROGRESS") {
      handleCreateAccount();
    }
  }, []);

  return (
    <>
      {toasterData.message && toasterData.title && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}
      <div className="absolute top-8 left-1/2 flex -translate-x-1/2 gap-2 lg:hidden">
        <WalletIcon height={40} width={40} className="my-auto" />
        <Logo />
      </div>
      <div>
        {isCreatingAccount ? (
          <div className="mt-[-5rem] lg:ml-10 lg:flex">
            <div className="flex flex-col items-center">
              <h5 className="mb-4 text-4xl font-medium">Setting up Account</h5>
              <SpinnerComponent size={64} type={"primary"} showIcon={false} />
              <p className="mt-6 text-base font-normal text-neutral-600">
                We are currently creating your account.
              </p>
              <p className="mt-2 text-sm font-normal text-neutral-400">
                This should not take too long
              </p>
            </div>
          </div>
        ) : isAccountCreationFailed ? (
          <div className="mt-8">
            <div className="mb-4 flex items-center">
              <CloseCircle stroke="#DA291C" width={32} height={32} />
              <h4 className="ml-2 text-xl">Account Creation Failed</h4>
            </div>
            <p className="mb-2 text-[12px] text-primary-300">
              {accountCreationError || "We could not create your account."}
            </p>
            <p className="mb-8 text-[12px] text-primary-300">
              You can retry or discard and start over.
            </p>
            <div className="flex gap-4">
              <button
                className="secondary-button"
                onClick={handleDiscardAll}
                type="button"
              >
                Discard
              </button>
              <button
                className="primary-button"
                onClick={handleCreateAccount}
                type="button"
                disabled={isCreatingAccount}
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div>
              <h1 className="mb-4 text-4.5xl font-medium text-neutral-900">
                Additional Information
              </h1>
              <div className="mb-6 min-w-0 text-base text-neutral-600">
                Kindly enter the correct information
              </div>
            </div>
            <div className="space-y-4">
              <SelectComponent
                items={estimatedAnnualTurnoverItems}
                className="w-full rounded-xl"
                listboxZIndex={1300}
                label="Estimated Annual Turnover"
                id="estimated-annual-turnover"
                labelId="estimatedAnnualTurnover"
                placeholder="Select Estimated Annual Turnover"
                defaultValue=""
                value={formData.estimatedAnnualTurnover}
                hasError={formErrors.estimatedAnnualTurnover?.hasError}
                errorMessage={formErrors.estimatedAnnualTurnover?.message}
                onChange={(_event, newValue) =>
                  onFieldChange("estimatedAnnualTurnover", newValue)
                }
                onClose={() => onFieldBlur("estimatedAnnualTurnover")}
                editable={true}
              />
              <SelectComponent
                items={staffSizeItems}
                className="w-full rounded-xl"
                listboxZIndex={1300}
                label="Staff Size"
                id="staff-size"
                labelId="staffStrength"
                placeholder="Select Staff Size"
                defaultValue=""
                value={formData.staffStrength}
                hasError={formErrors.staffStrength.hasError}
                errorMessage={formErrors.staffStrength.message}
                onChange={(_event, newValue) =>
                  onFieldChange("staffStrength", newValue)
                }
                onClose={() => onFieldBlur("staffStrength")}
                editable={true}
              />
              <div
                className={`flex flex-col items-center justify-between gap-6 sm:flex-row`}
              >
                <ButtonComponent
                  label="Continue"
                  className="mt-8"
                  onClick={handleContinue}
                  disabled={!isFormValid || isSubmitting}
                  isLoading={isSubmitting}
                  size="large"
                  type="primary"
                  fullWidth
                  id="continue-button"
                />
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default SignUpBusinessInformationpage;
